import {
  type AllKeys,
  dateUtils,
  isEmpty,
  type OptionalRecord,
  type TypeForKey,
} from '@festina-life/core'
import dayjs from 'dayjs'
import get from 'lodash-es/get'
import {
  isValidElement,
  type JSX,
  type MouseEvent,
  type ReactElement,
} from 'react'

import type { ColumnDefinition, SortOptions } from '../types'

export type ColumnId = string

export type Column<T> = {
  heading: string | JSX.Element | undefined
  width: string | undefined
  keyPath: string | number | undefined
  alignment: 'left' | 'right' | 'center' | undefined
  onCellClick: ((val: T, event: MouseEvent<HTMLElement>) => void) | undefined
  sortable: boolean | undefined
  sortOptions: SortOptions | undefined
  filterMode:
    | undefined
    | 'auto'
    | 'numberRange'
    | 'dateRange'
    | {
        type: 'radio'
        options: { label: string; filter: (datum: T) => boolean }[]
      }
  tdClassName: ((val: T) => string | undefined) | undefined
  noWrap: boolean | undefined
  getCellValue: (row: T) => unknown
  getSortValue: (row: T) => unknown
  getCellDisplay: (row: T) => ReturnType<typeof format>
  /**
   * Used when `getCellValue` returns an array and
   * those untyped items need to be displayed individually.
   *
   * At the moment, this is only used for `'auto'` filters,
   * where values are flattened into one separate option each.
   *
   * The default implementation matches that of `getCellDisplay`,
   * but is wrapped in a `try/catch` to `return null` on failure,
   * as the formatter might not be prepared to work on that type.
   * */
  getValueDisplay: (value: unknown) => ReturnType<typeof format>
}

function toCellValue<
  T extends OptionalRecord,
  K extends AllKeys<T> = AllKeys<T>,
>(col: ColumnDefinition<T, K>, record: T): unknown {
  const value = col.keyPath ? get(record, col.keyPath) : record
  return col.sortValue ? col.sortValue(value) : value
}

export function toSortable<
  T extends OptionalRecord,
  K extends AllKeys<T> = AllKeys<T>,
>(col: ColumnDefinition<T, K>, record: T): unknown {
  const value = col.keyPath ? get(record, col.keyPath) : record
  if (col.sortValue) {
    return col.sortValue(value)
  }
  try {
    return isEmpty(value) ? value : format(col, record)
  } catch {
    return value
  }
}

const defaultFormatter = (
  x: unknown,
  keyPath: unknown,
): string | number | ReactElement => {
  if (isEmpty(x)) {
    return '-'
  }

  if (
    (typeof x === 'string' &&
      dayjs(x, dateUtils.SERVER_DATE_FORMAT, true).isValid()) ||
    x instanceof Date
  ) {
    return dayjs(x).format('L')
  }

  if (typeof x === 'number' || typeof x === 'string') {
    return x
  }

  if (Array.isArray(x)) {
    return x.join(', ')
  }

  if (isValidElement(x)) {
    return x
  }

  // We lack the knowledge which the component using the table has to correctly format this value.
  // The component really should provide a custom formatter to correctly display this value.
  // To make it easier to find and fix this problem, an error is thrown, rather than returning
  // a placeholder (which may in some cases look "correct"). It might be possible to detect this
  // during type-checking and not at runtime, but for now this is a good middle-ground.
  throw new Error(
    `Cannot apply a sensible default formatting to ${keyPath} with mapped value: ${JSON.stringify(
      x,
    )}`,
  )
}

export const format = <
  T extends OptionalRecord,
  K extends AllKeys<T> = AllKeys<T>,
>(
  col: ColumnDefinition<T, K>,
  row: T,
) =>
  col.formatter
    ? col.formatter(col.keyPath ? get(row, col.keyPath) : row)
    : defaultFormatter(col.keyPath ? get(row, col.keyPath) : row, col.keyPath)

export const resolveColumn = <
  TRowData extends OptionalRecord,
  TKeys extends AllKeys<TRowData>,
>(
  column: ColumnDefinition<TRowData, TKeys>,
): Column<TRowData> => ({
  heading: column.heading,
  width: column.width,
  keyPath: column.keyPath,
  alignment: column.alignment,
  onCellClick: column.onCellClick,
  sortable: column.sortable,
  sortOptions: column.sortOptions,
  filterMode: column.filterMode,
  tdClassName: column.tdClassName,
  noWrap: column.noWrap,
  getCellValue: (row: TRowData) => toCellValue(column, row),
  getSortValue: (row: TRowData) => toSortable(column, row),
  getCellDisplay: (row: TRowData) => format(column, row),
  getValueDisplay: (value: unknown) => {
    try {
      // We are deliberately passing garbage to the `formatter`.
      //
      // `getValueDisplay` is only used with `'auto'` filters whose `getCellValue`
      // returns an `instanceof Array` that contains untyped items that must be displayed.
      //
      // If a value cannot be displayed, the option simply won't be presented.
      return column.formatter
        ? column.formatter(value as TypeForKey<TRowData, TKeys>)
        : defaultFormatter(value, undefined)
    } catch {
      return null
    }
  },
})

export const createColumnDefiner = <TRowData extends OptionalRecord>(): (<
  TKeys extends AllKeys<TRowData>,
>(
  columnDefinition: ColumnDefinition<TRowData, TKeys>,
) => Column<TRowData>) =>
  // Generally definers should be created outside the body of React components,
  // but this is not always possible (e.g. the component involves generic types.)
  //
  // Because we want both a stable reference and to be usable outside components,
  // we cannot `useCallback` to achieve the stable reference.
  // We instead declare the definer as a free-standing function and return that.
  // The types are compatible, but TypeScript can't know that without having to
  // introduce an unstable wrapper function. We also can't refer to `TKeys` here.
  //
  // oxlint-disable-next-line typescript/no-explicit-any -- we know the type is compatible
  resolveColumn as any
